import { useState, useEffect, useCallback, useMemo } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { BrushStart } from "../../../models/chart_model";
import { RajasthanDiscomDrawal } from "../../../models/rajasthan";
import {
  getRajasthanDiscomDrawlData,
  getRajasthanDiscomDrawlForecastData,
} from "../Utility/RestApi";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { getMonthFromDate } from "../../../extensions/date";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { DEMAND_UNITS } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import Tooltip from "../../../components/tooltip";
import Papa, { Parser, ParseResult } from "papaparse";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanDrawalPeakAnalysis } from "./Drawal_TrendPeak";
import { RajasthanDrawalSummaryAnalysis } from "./Drawal_TrendSummary";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { getRestrictedTrendMetrics } from "../Utility/RestApi";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";
// ─── Constants ────────────────────────────────────────────────────────────────

const GROUP_BY = [
  { label: "Area", key: "area", values: ["station", "discom"] },
  { label: "Discom", key: "discom", values: ["discom"] },
  { label: "District", key: "district", values: ["district", "district_only"] },
  { label: "Total", key: "total", values: ["total"] },
];

const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL", apiKey: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL", apiKey: "JVVNL" },
  { label: "AVVNL", key: "AVVNL", apiKey: "AVVNL" },
];

const INTERVALS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  { label: "Date", key: "dateIndex", values: ["dateIndex"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = DEMAND_UNITS;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDisplayDiscomName(apiKey: string): string {
  return DISCOM.find((d) => d.apiKey === apiKey)?.label ?? apiKey;
}

function makeLabel(name: string | undefined, discom: string): string {
  const discomName = getDisplayDiscomName(discom);
  const cleanName = (name ?? "").toString().trim();
  if (!discomName && !cleanName) return "Unknown";
  if (!cleanName) return discomName || "Unknown";
  if (!discomName) return cleanName;
  return cleanName.includes("undefined")
    ? discomName
    : `${cleanName} (${discomName})`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RajasthanDrawalTrend() {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<any[]>([]);
  const [fileForecastData, setFileForecastData] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[1]]);
  const [selectedInterval, setSelectedInterval] = useState(INTERVALS[0]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<TrendData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  // ─── Handlers ────────────────────────────────────────────────────────────────

  function handleGroupByChange(index: number) {
    setSelectedGroupKeys([GROUP_BY[index]]);
    setSelectedDistricts([]);
    setSelectedArea([]);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  // ─── Forecast file upload ─────────────────────────────────────────────────

  function handleForecastFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    let allData: any[] = [];
    setLoading(true);

    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      worker: true,
      chunk: (results: ParseResult<any>, _parser: Parser) => {
        allData = allData.concat(
          results.data.map((row: any) => {
            const mw = parseFloat(row.predicted_demand) || 0;
            return {
              date: row.date,
              block: row.block,
              area: row.area,
              predicted_demand: mw,
              mw,
              mwh: mw / 4,
              lu: mw / 400,
              discom: "Total",
              month: new Date(row.date).getMonth() + 1,
            };
          }),
        );
      },
      complete: () => {
        setFileForecastData(allData);
        setLoading(false);
      },
      error: (error: any) => {
        console.error("Error parsing forecast CSV:", error);
        setLoading(false);
      },
    });
  }

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates?.length) {
      setMetrics(null);
      setMetricsLoading(false);
      return;
    }
    setMetricsLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;

      const ranges = originalDates
        .filter((date: any) => {
          // Skip forecast entries — value has 3 parts: "start,end,Model 1"
          const commaParts = date.value.split(",");
          return commaParts.length <= 2;
        })
        .map((date: any) => {
          let start: string;
          let end: string;

          if (date.value.includes(" to ")) {
            [start, end] = date.value
              .split(" to ")
              .map((s: string) => s.trim());
          } else {
            const parts = date.value.split(",").map((s: string) => s.trim());
            start = parts[0];
            end = parts[1] ?? parts[0];
          }

          return {
            startDate: formatDateForAPI(new Date(start)),
            endDate: formatDateForAPI(new Date(end)),
            blocks: selectedBlock.map((block: any) => block.value),
          };
        })
        .filter((range: any) => range.startDate && range.endDate);
      if (ranges.length === 0) {
        setMetrics(null);
        setMetricsLoading(false);
        return;
      }

      const metricsData = await getRestrictedTrendMetrics({
        ranges,
        aggregated: true,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock]);
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const fetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) return;

    setLoading(true);

    try {
      const dates = DefaultDates[activeTab].defaultTrendDates;

      const results = await Promise.all(
        dates.map(async (date: any) => {
          const [startDate, endDate, model] = date.value.split(",");

          if (model) {
            const forecast = await getRajasthanDiscomDrawlForecastData({
              startDate,
              endDate,
            });
            return forecast.map((item: any) => ({
              ...item,
              dates: date.value,
            }));
          }

          const rangeData = await getRajasthanDiscomDrawlData({
            startDate,
            endDate,
          });
          return rangeData.map((item: any) => ({ ...item, dates: date.label }));
        }),
      );

      setData(results.flat());
    } catch (err) {
      console.error("Error fetching drawl data:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, DefaultDates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Data processing ──────────────────────────────────────────────────────

  const processedData = useMemo(() => {
    return data.map((item) => {
      let monthStr = "";
      if (item.date) {
        const d = new Date(item.date);
        const shortMonth = d.toLocaleDateString("en-US", { month: "short" });
        const shortYear = d.getFullYear().toString().slice(-2);
        monthStr = `${shortMonth}-${shortYear}`;
      }
      const mw = item.mw || 0;
      const station = makeLabel(item.station || item.area, item.discom);
      return {
        ...item,
        station,
        mw,
        mwh: mw / 4,
        lu: mw / 400,
        month: monthStr || item.month,
      };
    });
  }, [data]);

  const discomFilteredData = useMemo(() => {
    if (selectedDiscom.length === 0) return processedData;
    return processedData.filter((item) => selectedDiscom.includes(item.discom));
  }, [processedData, selectedDiscom]);

  // Available districts (based on discom-filtered data)
  const uniqueDistrict = useMemo(() => {
    return Array.from(
      new Set(
        discomFilteredData.map((item) =>
          makeLabel(item.district || item.area, item.discom),
        ),
      ),
    )
      .filter((v) => v && v !== "Unknown")
      .sort();
  }, [discomFilteredData]);

  const districtFilteredData = useMemo(() => {
    if (selectedDistricts.length === 0) return discomFilteredData;
    const distValues = selectedDistricts.map((d: any) => d.value);
    return discomFilteredData.filter((item) =>
      distValues.includes(makeLabel(item.district || item.area, item.discom)),
    );
  }, [discomFilteredData, selectedDistricts]);

  // Available areas — use `station` (GSS name) not `area` (divisional HQ)
  const uniqueArea = useMemo(() => {
    return Array.from(
      new Set(
        districtFilteredData.map((item) =>
          makeLabel(item.station || item.area, item.discom),
        ),
      ),
    )
      .filter((v) => v && v !== "Unknown")
      .sort();
  }, [districtFilteredData]);

  const { chartData, TableData, chartKeys } = useMemo(() => {
    let res = districtFilteredData;

    // Area filter — match against station (GSS name)
    if (selectedArea.length > 0) {
      const areaValues = selectedArea.map((a: any) => a.value);
      res = res.filter((item) =>
        areaValues.includes(makeLabel(item.station || item.area, item.discom)),
      );
    }

    // Block filter
    if (selectedBlock.length > 0) {
      const blockValues = selectedBlock.map((opt) => opt.value);
      res = res.filter((item) => blockValues.includes(item.block));
    }

    // Determine groupBy key
    // When area is selected → group by station (GSS-level), not area (HQ-level)
    const groupByKeys =
      selectedArea.length > 0
        ? ["station"]
        : selectedDistricts.length > 0
          ? ["district"]
          : selectedGroupKeys.map((g) => g.key.toLowerCase());

    const par: ChartDataParams = {
      keys:
        selectedInterval.key === "dateIndex"
          ? ["dateIndex"]
          : selectedInterval.values,
      tableKeys: ["dateIndex", "block", "month"],
      groupBy: ["dates"].concat(groupByKeys),
      volumeKey: "mwh",
      aggregationKeys: ["lu"],
      averageKeys: ["mw"],
      averageDuplicationKeys: ["dateIndex", "block"],
      priceKey: "",
      data: res,
    };

    const cData = FormatChartData(par);
    return {
      chartData: cData.data || [],
      TableData: cData.tableData || [],
      chartKeys: cData.keys,
    };
  }, [
    districtFilteredData,
    selectedArea,
    selectedBlock,
    selectedInterval,
    selectedGroupKeys,
    selectedDistricts.length,
  ]);

  // ─── Legends ──────────────────────────────────────────────────────────────

  const legends = useMemo(() => {
    return chartKeys.map((key) => {
      const datePart = key.split("_")[0];
      let name = key.includes("_") ? key.split("_")[1] : "Demand";

      name = (name || "").replace(/ \(.*?\)$/, "");

      if (!name || ["undefined", "", "total"].includes(name.toLowerCase())) {
        name = "Demand";
      }

      const displayName =
        datePart && datePart !== "undefined"
          ? `${datePart} ${name}`.trim()
          : name.trim();

      return {
        dataKey: `${key}_${selectedUnit.toLowerCase()}`,
        name: displayName,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined
            ? selectedChartType.stackId
            : undefined,
      };
    });
  }, [chartKeys, selectedUnit, selectedChartType]);

  // ─── Summary helpers ──────────────────────────────────────────────────────

  const summaryExtraKeys = useCallback((): string[] => {
    if (!selectedInterval.isTime) return [selectedInterval.values[0]];
    return selectedInterval.key === "month"
      ? ["month"]
      : ["dateIndex", "block"];
  }, [selectedInterval]);

  const summaryExtraLabels = useCallback((): string[] => {
    if (!selectedInterval.isTime) return [selectedInterval.values[0]];
    return selectedInterval.key === "month"
      ? ["Month"]
      : ["Date Index", "Block"];
  }, [selectedInterval]);

  const xDataKey = useMemo(() => {
    return selectedInterval.key === "time" ? "block" : selectedInterval.key;
  }, [selectedInterval]);

  // ─── Legend filter ────────────────────────────────────────────────────────

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  // ─── Table props ──────────────────────────────────────────────────────────

  const parseDateIndex = (item: any) => ({
    ...item,
    dateIndex:
      item.dateIndex && typeof item.dateIndex === "string"
        ? parseInt(item.dateIndex.replace("Day", ""))
        : item.dateIndex || 0,
  });

  const commonSummaryProps = {
    legends: visibleLegends as any,
    data: chartData.map(parseDateIndex),
    tableData: TableData.map(parseDateIndex),
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Trend Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  const sharedProps = {
    onXAxisModeChange: handleXAxisModeChange,
    selectedUnit,
    onUnitChange: setSelectedUnit,
    selectedDiscom,
    onDiscomChange: setSelectedDiscom,
    selectedGroupKeys,
    onGroupByChange: handleGroupByChange,
    selectedArea,
    onAreaChange: setSelectedArea,
    selectedDistricts,
    onDistrictsChange: setSelectedDistricts,
    metrics,
  };

  // ─── Mode routing ─────────────────────────────────────────────────────────

  if (selectedXAxisMode.value === "peak") {
    return <RajasthanDrawalPeakAnalysis {...sharedProps} />;
  }

  if (selectedXAxisMode.value === "summary") {
    return <RajasthanDrawalSummaryAnalysis {...sharedProps} />;
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((i) => i.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Interval"
            items={INTERVALS.map((i) => i.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(_, index) => setSelectedInterval(INTERVALS[index])}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => setSelectedUnit(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((i) => i.label)}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((i) => i.label)}
            selectedItems={selectedGroupKeys.map((i) => i.label)}
            onSelect={(_, index) => handleGroupByChange(index)}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((i) => i.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, index) => setSelectedChartType(R_CHART_TYPES[index])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-60">
                  Restricted Demand ({selectedUnit})
                </h2>

                <div className="flex gap-x-6 justify-end">
                  {selectedGroupKeys[0]?.key === "district" && (
                    <div className="min-w-40">
                      <SearchBox
                        options={uniqueDistrict.map((v) => ({
                          value: v,
                          label: v,
                        }))}
                        isMany={true}
                        placeholder="Search District"
                        selectedOptions={selectedDistricts}
                        onChange={(v: any) => {
                          setSelectedDistricts(v);
                          setSelectedArea([]);
                        }}
                      />
                    </div>
                  )}

                  {(selectedGroupKeys[0]?.key === "area" ||
                    (selectedGroupKeys[0]?.key === "district" &&
                      selectedDistricts.length > 0)) && (
                    <div className="min-w-40">
                      <SearchBox
                        options={uniqueArea.map((v) => ({
                          value: v,
                          label: v,
                        }))}
                        isMany={true}
                        placeholder="Search Area"
                        selectedOptions={selectedArea}
                        onChange={(v: any) => setSelectedArea(v)}
                      />
                    </div>
                  )}

                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((i) => i.value)}
                    setSelectedOptions={setSelectedBlock}
                  />

                  <button
                    onClick={() => {
                      if (dataTab.label === "Summary")
                        downloadSummaryCSV(commonSummaryProps);
                      else downloadChartTableCSV(commonSummaryProps);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={
                    selectedBlock.length !== 0 &&
                    selectedInterval.key === "dateIndex"
                  }
                  barCategoryGap={selectedInterval.isTime ? 2 : 40}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => renderHourTick(value, 10)
                      : undefined
                  }
                  secondXDataKey={
                    selectedInterval.isTime && selectedBlock.length > 0
                      ? "dateIndex"
                      : selectedInterval.values[1]
                  }
                />
              )}

              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...commonSummaryProps} />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>GSS Report (RUVITL)</p>}
              text="1. Sum of all GSS load = Actual drawal (Restricted demand)
2. MW = Represents the average (MW) when the selected time interval is Date or Month."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
